/**
 * Phase of the room clock for a consumer running at its own period, expressed in
 * [0,1). `offsetPct` is a fraction of that period (1.0 = a full period), so it
 * shifts the phase exactly like emitter offset does. Deterministic in `elapsed`
 * alone, so any two consumers with the same period and offset stay phase-locked.
 */
export function phaseAt(elapsed: number, periodSec: number, offsetPct: number): number {
  const cycles = elapsed / periodSec + offsetPct;
  return cycles - Math.floor(cycles);
}

export function periodFromPercent(basePeriodSec: number, pct: number): number {
  return basePeriodSec * pct;
}

/**
 * Clock time wrapped to one period, in seconds [0, periodSec). Feeding this to
 * the emit-RNG shader instead of raw elapsed keeps the hash time term small
 * (float precision does not degrade as elapsed grows) and makes the emit RNG a
 * pure function of slot uv × phase-in-period, so spread patterns repeat every
 * clock period.
 */
export function wrappedClockTime(elapsed: number, periodSec: number): number {
  return phaseAt(elapsed, periodSec, 0) * periodSec;
}

/**
 * Length of one sequencer cell in seconds. The clock period is one quarter-note
 * beat (60 / BPM) and a cell is fixed at an eighth note — half a beat — so the
 * cell grid is independent of any emitter's meter or pattern length.
 */
export function cellSec(periodSec: number): number {
  return periodSec / 2;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * One full performance cycle in eighth-note cells: the least common multiple of
 * every placed sequencer's pattern length (its `num`), i.e. the interval at
 * which all patterns realign on their first cell. One 8-cell 4/4 bar alone → 8;
 * adding a 6-cell 3/4 bar → 24 (three 4/4 bars = four 3/4 bars, the polymeter
 * loop). 1 when no sequencer is placed (the caller falls back to the clock
 * period then).
 */
export function performanceCycleCells(nums: readonly number[]): number {
  let cycle = 1;
  for (const n of nums) {
    cycle = (cycle * n) / gcd(cycle, n);
  }
  return cycle;
}

/**
 * Length of one full performance cycle in seconds: the sequencer-pattern LCM in
 * cells times the cell length, falling back to one clock period when no
 * sequencer is placed (the same fallback every phase consumer uses).
 */
export function performanceCycleSec(seqNums: readonly number[], periodSec: number): number {
  return seqNums.length > 0 ? performanceCycleCells(seqNums) * cellSec(periodSec) : periodSec;
}
